/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';

import TitleBox from '../../components/common/breadcrumbs/TitleBox';
import DetailTemplate from '../../components/common/template/DetailTemplate';
import BaseCodeSelectbox from '../../components/common/form/selectbox/BaseCodeSelectbox';
import TextMaskBusinessNo from '../../components/common/form/TextMaskBusinessNo';
import * as partnerApi from '../../lib/api/partner';
import { publishPartnerCreated } from '../../utils/partnerCreatedEvent';

// 빈 담당자 행
const emptyEmployee = () => ({
  _id: Date.now() + Math.random(), // 임시 key
  employeeNm: '',
  employeeGrade: '',
  dutiesNm: '',
  telNo: '',
  emailAddr: '',
  employeeDesc: '',
});

const PartnerAddContainer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  // 담당자 목록 state
  const [employees, setEmployees] = useState([]);

  const returnParams = new URLSearchParams(location.search);
  const returnTo = returnParams.get('returnTo') || '';
  const returnToken = returnParams.get('returnToken') || '';
  const shouldReturnToProjectForm = returnTo === 'projectForm' && returnToken;

  const handleGoBack = () => navigate('/partnermng');

  // 기본정보 formik
  const formik = useFormik({
    initialValues: {
      partnerTyp: '',
      incTyp: '',
      tradeTyp: [],
      partnerNm: '',
      loanAmt: '0',
      regNumber: '',
      partnerPhone: '',
      partnerAddr: '',
      partnerDesc: '',
    },
    validationSchema: yup.object({
      partnerTyp: yup.string().required('거래처 종류를 선택하세요'),
      incTyp: yup.string().required('산업체 유형을 선택하세요'),
      partnerNm: yup.string().required('거래처명을 입력하세요'),
      loanAmt: yup.string().required('여신한도를 입력하세요'),
      regNumber: yup
        .string()
        .required('사업자번호를 입력하세요')
        .min(12, '사업자번호를 확인하세요')
        .max(12, '사업자번호를 확인하세요'),
      partnerPhone: yup.string().required('연락처를 입력하세요'),
      partnerAddr: yup.string().required('주소를 입력하세요'),
      partnerDesc: yup.string().nullable(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        // 1) 거래처 기본정보 저장
        const partnerRes = await partnerApi.addPartner({
          ...values,
          loanAmt: values.loanAmt || 0,
          tradeTyp: values.tradeTyp?.join(',') || '',
        });

        if (partnerRes.data.result !== 'success') {
          enqueueSnackbar(partnerRes.data?.message || '거래처 등록 실패', { variant: 'error' });
          setSubmitting(false);
          return;
        }

        const partnerUuid = partnerRes.data?.info?.partnerUuid;
        if (!partnerUuid) {
          throw new Error('등록된 거래처 UUID를 확인할 수 없습니다.');
        }

        // 2) 담당자 순차 저장
        const validEmployees = employees.filter((e) => e.employeeNm && e.telNo && e.emailAddr);
        for (const emp of validEmployees) {
          const { _id, ...empData } = emp;
          await partnerApi.addPartnerEmp(partnerUuid, empData);
        }

        enqueueSnackbar(
          validEmployees.length > 0
            ? `거래처와 담당자 ${validEmployees.length}명이 등록되었습니다`
            : '거래처가 등록되었습니다',
          { variant: 'success' }
        );

        if (shouldReturnToProjectForm) {
          publishPartnerCreated({
            partner: {
              ...partnerRes.data.info,
              partnerUuid,
              partnerNm: partnerRes.data.info?.partnerNm || values.partnerNm,
            },
            returnTo,
            returnToken,
          });

          setTimeout(() => {
            window.close();
            setTimeout(() => {
              navigate(`/partnermng/${partnerUuid}`);
            }, 200);
          }, 200);
          return;
        }

        navigate(`/partnermng/${partnerUuid}`);
      } catch (err) {
        const message = err.response?.data?.message || err.message;
        enqueueSnackbar(message, { variant: 'error' });
      }
      setSubmitting(false);
    },
  });

  // 담당자 행 추가
  const handleAddEmployee = () => {
    setEmployees((prev) => [...prev, emptyEmployee()]);
  };

  // 담당자 행 필드 변경
  const handleEmpChange = (_id, field, value) => {
    setEmployees((prev) =>
      prev.map((e) => (e._id === _id ? { ...e, [field]: value } : e))
    );
  };

  // 담당자 행 삭제
  const handleEmpDelete = (_id) => {
    setEmployees((prev) => prev.filter((e) => e._id !== _id));
  };

  return (
    <>
      <TitleBox
        handleGoBack={handleGoBack}
        menuName="마스터/거래처 관리/추가"
        menuLink="/partnermng"
        icon="partner"
        titleData={{ name: '거래처 신규 추가' }}
      />

      <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ mt: 2 }}>

        {/* 기본정보 */}
        <DetailTemplate title="기본정보">
          <Grid container spacing={1} sx={{ px: 1, pt: 1 }}>

            {/* 거래처 종류 / 산업체 유형 */}
            <Grid item xs={12} container alignItems="center" spacing={1}
              sx={{ py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
              <Grid item xs={3}><FieldLabel>거래처 종류</FieldLabel></Grid>
              <Grid item xs={3}>
                <BaseCodeSelectbox
                  formik={formik} variant="outlined" required fullWidth size="small"
                  filterObject={{ blcCd: 'BASE', mlcCd: 'PARTNER', slcCd: 'PARTNER_TYP', useYn: 'Y' }}
                  name="partnerTyp"
                />
              </Grid>
              <Grid item xs={3}><FieldLabel>산업체 유형</FieldLabel></Grid>
              <Grid item xs={3}>
                <BaseCodeSelectbox
                  formik={formik} variant="outlined" required fullWidth size="small"
                  filterObject={{ blcCd: 'BASE', mlcCd: 'FORCAST', slcCd: 'INC_TYP', useYn: 'Y' }}
                  name="incTyp"
                />
              </Grid>
            </Grid>

            {/* 사업자번호 */}
            <Grid item xs={12} container alignItems="center" spacing={1}
              sx={{ py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
              <Grid item xs={3}><FieldLabel>사업자번호</FieldLabel></Grid>
              <Grid item xs={3}>
                <TextField
                  size="small" variant="outlined" required fullWidth
                  id="regNumber" name="regNumber"
                  value={formik.values.regNumber}
                  onChange={(e) => formik.setFieldValue('regNumber', e.target.value)}
                  error={formik.touched.regNumber && Boolean(formik.errors.regNumber)}
                  helperText={formik.touched.regNumber && formik.errors.regNumber}
                  InputProps={{ inputComponent: TextMaskBusinessNo }}
                />
              </Grid>
            </Grid>

            {/* 거래처명 / 여신한도 */}
            <Grid item xs={12} container alignItems="center" spacing={1}
              sx={{ py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
              <Grid item xs={3}><FieldLabel>거래처명</FieldLabel></Grid>
              <Grid item xs={3}>
                <TextField
                  size="small" variant="outlined" required fullWidth
                  id="partnerNm" name="partnerNm"
                  value={formik.values.partnerNm}
                  onChange={formik.handleChange}
                  error={formik.touched.partnerNm && Boolean(formik.errors.partnerNm)}
                  helperText={formik.touched.partnerNm && formik.errors.partnerNm}
                />
              </Grid>
              <Grid item xs={3}><FieldLabel>여신한도</FieldLabel></Grid>
              <Grid item xs={3}>
                <TextField
                  size="small" variant="outlined" required fullWidth
                  id="loanAmt" name="loanAmt"
                  value={formik.values.loanAmt}
                  onChange={formik.handleChange}
                  error={formik.touched.loanAmt && Boolean(formik.errors.loanAmt)}
                  helperText={formik.touched.loanAmt && formik.errors.loanAmt}
                />
              </Grid>
            </Grid>

            {/* 연락처 */}
            <Grid item xs={12} container alignItems="center" spacing={1}
              sx={{ py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
              <Grid item xs={3}><FieldLabel>연락처</FieldLabel></Grid>
              <Grid item xs={9}>
                <TextField
                  size="small" variant="outlined" required fullWidth
                  id="partnerPhone" name="partnerPhone"
                  value={formik.values.partnerPhone}
                  onChange={formik.handleChange}
                  error={formik.touched.partnerPhone && Boolean(formik.errors.partnerPhone)}
                  helperText={formik.touched.partnerPhone && formik.errors.partnerPhone}
                />
              </Grid>
            </Grid>

            {/* 주소 */}
            <Grid item xs={12} container alignItems="center" spacing={1}
              sx={{ py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
              <Grid item xs={3}><FieldLabel>주소</FieldLabel></Grid>
              <Grid item xs={9}>
                <TextField
                  size="small" variant="outlined" required fullWidth
                  id="partnerAddr" name="partnerAddr"
                  value={formik.values.partnerAddr}
                  onChange={formik.handleChange}
                  error={formik.touched.partnerAddr && Boolean(formik.errors.partnerAddr)}
                  helperText={formik.touched.partnerAddr && formik.errors.partnerAddr}
                />
              </Grid>
            </Grid>

            {/* 참고사항 */}
            <Grid item xs={12} container alignItems="center" spacing={1} sx={{ py: 1 }}>
              <Grid item xs={3}><FieldLabel>참고사항</FieldLabel></Grid>
              <Grid item xs={9}>
                <TextField
                  size="small" variant="outlined" fullWidth multiline rows={3}
                  id="partnerDesc" name="partnerDesc"
                  value={formik.values.partnerDesc}
                  onChange={formik.handleChange}
                />
              </Grid>
            </Grid>

          </Grid>
        </DetailTemplate>

        {/* 담당자 */}
        <DetailTemplate title="담당자 (선택사항)">
          <Box sx={{ px: 1, pt: 1 }}>

            {/* 담당자 테이블 */}
            {employees.length > 0 && (
              <Table size="small" sx={{ mb: 1 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell>담당자명 *</TableCell>
                    <TableCell>직급</TableCell>
                    <TableCell>담당업무</TableCell>
                    <TableCell>연락처 *</TableCell>
                    <TableCell>이메일 *</TableCell>
                    <TableCell>비고</TableCell>
                    <TableCell width={40} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((emp) => (
                    <TableRow key={emp._id}>
                      <TableCell>
                        <TextField
                          size="small" variant="standard" fullWidth required
                          value={emp.employeeNm}
                          onChange={(e) => handleEmpChange(emp._id, 'employeeNm', e.target.value)}
                          error={!emp.employeeNm}
                          placeholder="필수"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" variant="standard" fullWidth
                          value={emp.employeeGrade}
                          onChange={(e) => handleEmpChange(emp._id, 'employeeGrade', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" variant="standard" fullWidth
                          value={emp.dutiesNm}
                          onChange={(e) => handleEmpChange(emp._id, 'dutiesNm', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" variant="standard" fullWidth required
                          value={emp.telNo}
                          onChange={(e) => handleEmpChange(emp._id, 'telNo', e.target.value)}
                          error={!emp.telNo}
                          placeholder="필수"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" variant="standard" fullWidth required
                          value={emp.emailAddr}
                          onChange={(e) => handleEmpChange(emp._id, 'emailAddr', e.target.value)}
                          error={!emp.emailAddr}
                          placeholder="필수"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small" variant="standard" fullWidth
                          value={emp.employeeDesc}
                          onChange={(e) => handleEmpChange(emp._id, 'employeeDesc', e.target.value)}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton size="small" color="error" onClick={() => handleEmpDelete(emp._id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* 담당자 추가 버튼 */}
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddEmployee}
              sx={{ mt: employees.length > 0 ? 0 : 1 }}
            >
              담당자 추가
            </Button>

            {employees.length > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                * 담당자명, 연락처, 이메일은 필수입니다
              </Typography>
            )}
          </Box>
        </DetailTemplate>

        {/* 하단 버튼 */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1, mb: 3 }}>
          <Button variant="outlined" color="inherit" onClick={handleGoBack}>
            취소
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={formik.isSubmitting}
          >
            저장
          </Button>
        </Box>

      </Box>
    </>
  );
};

// 라벨 스타일 컴포넌트
const FieldLabel = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{
      textAlign: 'center',
      borderRight: (t) => `1px solid ${t.palette.divider}`,
      pr: 1,
    }}
  >
    {children}
  </Typography>
);

export default PartnerAddContainer;
